<template>
  <section class="lg:max-w-[600px] lg:mx-auto lg:!px-0">
    <StatusBar v-if="!isCheckoutDesktopFlow" />
    <section :class="{ 'h-full grow px-4': !isCheckoutDesktopFlow }">
      <h1 class="text-2xl md:text-4xl font-bold font-heading">Please select a seating time.</h1>
      <div v-if="settingStore.seating_time_text" class="py-2 text-base italic text-fg-disclaimer">
        {{ settingStore.seating_time_text }}
      </div>

      <div v-if="isLoading" role="status" class="sr-only">Saving, please wait...</div>
      <div class="flex flex-col">
        <SlotButton
          v-for="(seatingTime, index) in seatingTimes"
          :key="seatingTime.id"
          class="w-52 transition-opacity my-2 mx-2 p-2 rounded font-semibold text-sm font-button bg-button-guest-primary text-button-label enabled:hover:opacity-80 disabled:cursor-not-allowed disabled:bg-disabled"
          :class="`seating-time-${index + 1}`"
          :disabled="seatingTime.sold || isLoading"
          :id="`seating-time-${seatingTime.id}`"
          @click="selectSeatingTime(seatingTime)"
          guest
        >
          {{ selectedSeatingTimeId === seatingTime.id ? 'Saving...' : seatingTime.time }}
          <span v-if="seatingTime.sold"> ( SOLD OUT ) </span>
        </SlotButton>
      </div>

      <p
        v-if="ui.error.message"
        id="error-display"
        class="p-2 bg-red-200 text-red-600 border border-red-400 my-4 rounded"
        role="alert"
        aria-live="assertive"
      >
        {{ ui.error.message }}
      </p>
      <GuestLegalLink class="block lg:hidden" />
      <CheckoutBottomBar hideNextButton />
    </section>
    <GuestLegalLink class="hidden lg:block" />
  </section>
</template>

<script lang="ts">
import * as Sentry from '@sentry/vue'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import SlotButton from '@/common/components/atoms/SlotButton.vue'
import { StorePerformance, StorePerformanceSeatingTime } from '@/models'
import GuestLegalLink from '@/modules/guestUi/components/atoms/GuestLegalLink.vue'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { blankError, clearError, displayHandled } from '@/utils/errors'
import { sortBy } from '@/utils/utils'

import { PerformanceSeatingTimeId } from '@generated/types'

interface DisplaySeatingTime extends StorePerformanceSeatingTime {
  sold: boolean
}

export default defineComponent({
  components: {
    CheckoutBottomBar,
    GuestLegalLink,
    SlotButton,
    StatusBar,
  },
  data() {
    return {
      ui: {
        error: blankError(),
      },
      isLoading: false,
      selectedSeatingTimeId: null as PerformanceSeatingTimeId | null,
    }
  },

  async created() {
    if (!this.checkoutStore.performance) {
      Sentry.captureException(new Error(`Arrived at SetSeatingTime with no performance set`))
      await this.$router.replace('/')
      return
    }
    await this.checkoutStore.refreshPerformance()
  },

  computed: {
    ...mapStores(useSettingStore, useCheckoutStore),

    currentPerformance(): StorePerformance {
      if (!this.checkoutStore.performance) {
        this.$router.replace('/')
        throw new Error("Can't be in SetSeatingTime without a performance")
      }
      return this.checkoutStore.performance
    },

    seatingTimes(): DisplaySeatingTime[] {
      return sortBy(
        (this.currentPerformance.seating_times || []).map(seating_time => ({
          ...seating_time,
          sold:
            seating_time.maximum_guests - (seating_time.reserved_guests || 0) <
            this.checkoutStore.number_of_guests,
        })),
        'time24',
      )
    },

    isCheckoutDesktopFlow(): boolean {
      return this.checkoutStore.isDesktopFlow
    },
  },

  mounted() {
    this.checkoutStore.clearSeatingTime()
  },

  methods: {
    onBack() {
      history.go(-1)
    },
    async selectSeatingTime(seatingTime: DisplaySeatingTime) {
      this.selectedSeatingTimeId = seatingTime.id
      this.isLoading = true
      clearError(this.ui.error)
      try {
        await this.checkoutStore.setSeatingTime(seatingTime, this.ui.error)
      } catch (e) {
        displayHandled(e, this.ui.error)
        this.isLoading = false
        this.selectedSeatingTimeId = null
      }
    },
  },
})
</script>
